import {useState} from 'react';
import truncateStringByWords from 'utils/functions/truncateStringByWords';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {useParams} from 'react-router-dom';
import getFeatureFlag from 'utils/functions/getFeatureFlags';
import {getUserData} from 'utils/functions/getUserData/getUserData';
import {Button, Modal, useModals} from 'letrus-ui';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import styles from './ItemAccordeon.module.scss';
import {AccordeonItem} from '..';
import OrthographicDeviationsTable from './OrthographicDeviationsTable';

export interface OrthographicDeviation {
  text: string;
  suggested_text: string;
  comment: string;
}

export interface ItemAccordeonProps {
  positiveComment: string;
  attentionComment: string;
  evidenceHighlight: string;
  variant: AccordeonItem['variant'];
  competenceId: number;
  orthographicDeviations?: OrthographicDeviation[];
  shouldShowPracticeC5Button?: boolean;
  score?: number;
  isEnem?: boolean;
}

function ItemAccordeon({
  positiveComment,
  attentionComment,
  evidenceHighlight,
  variant,
  competenceId,
  orthographicDeviations,
  shouldShowPracticeC5Button,
  score,
  isEnem,
}: ItemAccordeonProps): JSX.Element {
  const [showFullDescriptionText, setShowFullDescriptionText] = useState(false);

  const {trackEvent} = useTrackEvent();
  const {composition_id} = useParams();
  const modals = useModals(['orthographicDeviation']);

  const formattedPositiveComment = positiveComment?.replace(/\n/g, '<br />');
  const formattedAttentionComment = attentionComment?.replace(/\n/g, '<br />');

  const shouldShowOrthographicsDeviationsButton =
    competenceId === 195 || competenceId === 97;

  const isBannerC5On = getFeatureFlag('c5_banner').isOn;

  const {user} = getUserData();

  return (
    <div className={`${styles.container} ${styles[variant]}`}>
      <div className={styles.competenceDescription}>
        <span>
          {(showFullDescriptionText || variant === 'positive') &&
            positiveComment?.length && (
              <>
                <h4>Pontos Positivos</h4>

                <p
                  dangerouslySetInnerHTML={{
                    __html: showFullDescriptionText
                      ? formattedPositiveComment
                      : truncateStringByWords(formattedPositiveComment, 20),
                  }}
                />
              </>
            )}

          {(showFullDescriptionText || variant !== 'positive') &&
            attentionComment?.length && (
              <>
                <h4>
                  {(isEnem && score === 200) || (!isEnem && score === 10)
                    ? 'Dicas extras'
                    : 'Como melhorar'}
                </h4>

                <p
                  dangerouslySetInnerHTML={{
                    __html: showFullDescriptionText
                      ? formattedAttentionComment
                      : truncateStringByWords(formattedAttentionComment, 20),
                  }}
                />
              </>
            )}
        </span>

        <div className={styles.buttons}>
          <Button
            onClick={() => {
              if (!showFullDescriptionText) {
                trackEvent('step3_feedback_competence_button_clicked', {
                  composition: {
                    composition_id: Number(composition_id),
                  },
                });
              }
              setShowFullDescriptionText(!showFullDescriptionText);
            }}
          >
            <FontAwesomeIcon icon="eye" />
            {showFullDescriptionText ? 'Ver menos' : 'Ver mais'}
          </Button>

          {!!orthographicDeviations?.length &&
            shouldShowOrthographicsDeviationsButton &&
            showFullDescriptionText && (
              <Button
                variant="secondary"
                onClick={() => modals.orthographicDeviation?.showModal()}
              >
                <FontAwesomeIcon icon="filter-list" />
                Ver desvios
              </Button>
            )}

          {shouldShowPracticeC5Button && isBannerC5On && (
            <Button
              variant="secondary"
              onClick={() => {
                window.open(
                  `https://c5.letrus.com.br?pk=${user?.id}`,
                  '_blank',
                );
              }}
            >
              <FontAwesomeIcon icon="pencil" />
              Praticar C5
            </Button>
          )}
        </div>
      </div>

      <p className={styles.competenceEvidence}>
        <b>Evidência: </b>
        <span
          className={styles.evidenceHighlight}
          dangerouslySetInnerHTML={{__html: evidenceHighlight}}
        />
      </p>

      <Modal id="orthographicDeviation">
        <OrthographicDeviationsTable
          orthographicDeviations={orthographicDeviations}
        />
      </Modal>
    </div>
  );
}

export default ItemAccordeon;
